import React from 'react';
import { Button } from '@sm/ide-components';
import './index.less';
import { InboundInit } from '@/types';
import { AddIcon, CloseIcon, FullScreenIcon, HistoryIcon, MagicAIIcon } from '@/assets/SVGIcon';
import { t } from '@/i18n';

/** 头部可切换的视图页签 */
export type HeaderTab = 'chat' | 'marketing';

interface ChatHeaderProps {
  /** 是否显示关闭按钮 */
  config?: InboundInit['payload']['config'];
  /** 关闭回调 */
  onClose?: () => void;
  onFullScreen?: () => void;
  onNewChat?: () => void;
  onShowHistory?: () => void;
  isHistoryView?: boolean;
  newChatDisabled?: boolean;
  historyDisabled?: boolean;
  /** 是否展示「门店AI营销」入口，由 ChatPanel 根据资格结果控制 */
  showStoreMarketing?: boolean;
  /** 当前激活的页签 */
  activeTab?: HeaderTab;
  onSelectTab?: (tab: HeaderTab) => void;
}

const ChatHeader: React.FC<ChatHeaderProps> = ({
  config,
  onClose,
  onFullScreen,
  onNewChat,
  onShowHistory,
  isHistoryView,
  newChatDisabled,
  historyDisabled,
  showStoreMarketing,
  activeTab = 'chat',
  onSelectTab,
}) => {
  const { close, fullScreen, history } = config || {};
  const isMarketing = activeTab === 'marketing';

  /** 页签支持键盘激活，保持与点击一致的可访问性 */
  const activateOnKey = (tab: HeaderTab) => (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onSelectTab?.(tab);
    }
  };

  /**
   * 没有营销入口时 Magic AI 只是标题：不加 --tab（手型/hover）也不加选中态，
   * 否则会出现"看起来能点、点了没反应"和常亮底色。
   */
  const brandClassName = [
    'chat-header__brand',
    showStoreMarketing ? 'chat-header__brand--tab' : '',
    showStoreMarketing && !isMarketing ? 'chat-header__brand--active' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const brandTabProps = showStoreMarketing
    ? {
        role: 'button' as const,
        tabIndex: 0,
        onClick: () => onSelectTab?.('chat'),
        onKeyDown: activateOnKey('chat'),
      }
    : {};

  return (
    <div className="chat-header">
      <div className="chat-header__left">
        <span className="chat-header__ai-icon" aria-hidden="true">
          <MagicAIIcon />
        </span>
        <span className={brandClassName} {...brandTabProps}>
          <span className="chat-header__title">Magic AI</span>
        </span>

        {showStoreMarketing && (
          <span
            className={`chat-header__tab${isMarketing ? ' chat-header__tab--active' : ''}`}
            role="button"
            tabIndex={0}
            title={t('header.storeMarketing')}
            onClick={() => onSelectTab?.('marketing')}
            onKeyDown={activateOnKey('marketing')}
          >
            <span className="chat-header__tab-label">{t('header.storeMarketing')}</span>
          </span>
        )}
      </div>

      <div className="chat-header__actions">
        {!isMarketing && history && (
          <Button
            type="text"
            size="small"
            icon={<AddIcon />}
            className="chat-header__btn"
            disabled={newChatDisabled}
            onClick={onNewChat}
          />
        )}

        {!isMarketing && history && (
          <Button
            type="text"
            size="small"
            icon={<HistoryIcon />}
            className={`chat-header__btn${isHistoryView ? ' chat-header__btn--active' : ''}`}
            disabled={historyDisabled}
            onClick={onShowHistory}
          />
        )}

        {fullScreen && (
          <Button
            type="text"
            size="small"
            icon={<FullScreenIcon />}
            className="chat-header__btn"
            onClick={onFullScreen}
          />
        )}

        {close && (
          <Button
            type="text"
            size="small"
            icon={<CloseIcon />}
            className="chat-header__btn"
            onClick={onClose}
          />
        )}
      </div>
    </div>
  );
};

export default ChatHeader;
